import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, Loader2, Sparkles, Settings, AlertCircle } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import { supabase } from '@/lib/supabase';
import type { MealItem } from '@/types';

interface MealItemEditorProps {
  open: boolean;
  item: MealItem | null;
  onClose: () => void;
  onSave: (item: MealItem) => void;
  onDelete?: () => void;
}

export function MealItemEditor({ open, item, onClose, onSave, onDelete }: MealItemEditorProps) {
  const { haptic } = useTelegram();
  const isNew = item === null;

  const [name, setName] = useState(item?.name ?? '');
  const [weight, setWeight] = useState(String(item?.weight_g ?? ''));
  const [calories, setCalories] = useState(String(item?.calories ?? ''));
  const [protein, setProtein] = useState(String(item?.protein_g ?? ''));
  const [fat, setFat] = useState(String(item?.fat_g ?? ''));
  const [carbs, setCarbs] = useState(String(item?.carbs_g ?? ''));

  const [micros, setMicros] = useState({
    fiber_g: item?.fiber_g ?? 0,
    vegetables_fruits_g: item?.vegetables_fruits_g ?? 0,
    omega3_g: item?.omega3_g ?? 0,
    saturated_fat_g: item?.saturated_fat_g ?? 0,
    calcium_mg: item?.calcium_mg ?? 0,
    iron_mg: item?.iron_mg ?? 0,
    magnesium_mg: item?.magnesium_mg ?? 0,
    zinc_mg: item?.zinc_mg ?? 0,
    potassium_mg: item?.potassium_mg ?? 0,
    vitamin_d_mcg: item?.vitamin_d_mcg ?? 0,
    vitamin_b12_mcg: item?.vitamin_b12_mcg ?? 0,
    vitamin_c_mg: item?.vitamin_c_mg ?? 0,
  });

  const [manualMode, setManualMode] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    if (open && item) {
      setName(item.name);
      setWeight(String(item.weight_g));
      setCalories(String(item.calories));
      setProtein(String(item.protein_g));
      setFat(String(item.fat_g));
      setCarbs(String(item.carbs_g));
      setMicros({
        fiber_g: item.fiber_g ?? 0,
        vegetables_fruits_g: item.vegetables_fruits_g ?? 0,
        omega3_g: item.omega3_g ?? 0,
        saturated_fat_g: item.saturated_fat_g ?? 0,
        calcium_mg: item.calcium_mg ?? 0,
        iron_mg: item.iron_mg ?? 0,
        magnesium_mg: item.magnesium_mg ?? 0,
        zinc_mg: item.zinc_mg ?? 0,
        potassium_mg: item.potassium_mg ?? 0,
        vitamin_d_mcg: item.vitamin_d_mcg ?? 0,
        vitamin_b12_mcg: item.vitamin_b12_mcg ?? 0,
        vitamin_c_mg: item.vitamin_c_mg ?? 0,
      });
      setManualMode(false);
      setAiError(null);
    } else if (open && !item) {
      setName('');
      setWeight('');
      setCalories('');
      setProtein('');
      setFat('');
      setCarbs('');
      setMicros({
        fiber_g: 0, vegetables_fruits_g: 0, omega3_g: 0, saturated_fat_g: 0,
        calcium_mg: 0, iron_mg: 0, magnesium_mg: 0, zinc_mg: 0, potassium_mg: 0,
        vitamin_d_mcg: 0, vitamin_b12_mcg: 0, vitamin_c_mg: 0,
      });
      setManualMode(false);
      setAiError(null);
    }
  }, [open, item]);

  const recalculateWithAI = async () => {
    if (!name.trim() || !weight) {
      setAiError('Введи название и вес');
      return;
    }
    const w = Number(weight);
    if (w < 1 || w > 5000) {
      setAiError('Вес должен быть от 1 до 5000г');
      return;
    }

    setRecalculating(true);
    setAiError(null);
    haptic('light');

    try {
      const { data, error } = await supabase.functions.invoke('recalculate-item', {
        body: { name: name.trim(), weight_g: w },
      });

      if (error) throw new Error(error.message || 'AI временно недоступен');
      if (data?.error) throw new Error(data.error);
      if (!data) throw new Error('Пустой ответ от AI');
      if (typeof data.calories !== 'number') throw new Error('AI вернул неверный формат');

      setCalories(String(Math.round(data.calories)));
      setProtein(String(data.protein_g));
      setFat(String(data.fat_g));
      setCarbs(String(data.carbs_g));
      setMicros({
        fiber_g: data.fiber_g ?? 0,
        vegetables_fruits_g: data.vegetables_fruits_g ?? 0,
        omega3_g: data.omega3_g ?? 0,
        saturated_fat_g: data.saturated_fat_g ?? 0,
        calcium_mg: data.calcium_mg ?? 0,
        iron_mg: data.iron_mg ?? 0,
        magnesium_mg: data.magnesium_mg ?? 0,
        zinc_mg: data.zinc_mg ?? 0,
        potassium_mg: data.potassium_mg ?? 0,
        vitamin_d_mcg: data.vitamin_d_mcg ?? 0,
        vitamin_b12_mcg: data.vitamin_b12_mcg ?? 0,
        vitamin_c_mg: data.vitamin_c_mg ?? 0,
      });

      haptic('success');
    } catch (err: any) {
      console.error('[MealItemEditor] recalc failed:', err);
      setAiError(err.message || 'Не удалось пересчитать');
      haptic('error');
    } finally {
      setRecalculating(false);
    }
  };

  const handleApply = () => {
    if (!name.trim()) {
      setAiError('Введи название');
      return;
    }
    const w = Number(weight);
    const cal = Number(calories);
    
    if (!w || w < 1) {
      setAiError('Введи вес');
      return;
    }
    if (cal === undefined || isNaN(cal) || cal < 0) {
      setAiError('Введи калории');
      return;
    }

    const updated: MealItem = {
      ...item,
      name: name.trim(),
      weight_g: w,
      calories: cal,
      protein_g: Number(protein) || 0,
      fat_g: Number(fat) || 0,
      carbs_g: Number(carbs) || 0,
      ...micros,
    };

    onSave(updated);
  };

  const handleDelete = () => {
    if (!confirm(`Удалить «${item?.name}»?`)) return;
    if (onDelete) onDelete();
  };

  const canApply = !recalculating && 
                   name.trim().length > 0 && 
                   weight !== '' && 
                   Number(weight) > 0 && 
                   calories !== '' && 
                   Number(calories) >= 0;

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 50, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-bg-card border border-line/40 rounded-2xl w-full max-w-md mx-auto my-8 mb-32"
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}
          <div className="px-5 pt-5 pb-3 border-b border-line/40">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg">
                {isNew ? 'Добавить продукт' : 'Изменить продукт'}
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-bg-elevated text-ink-muted flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* СОДЕРЖИМОЕ */}
          <div className="px-5 py-4 space-y-3">
            {/* Название */}
            <div>
              <label className="text-[11px] text-ink-dim block mb-1 uppercase tracking-wider">
                Название
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например: Греческий йогурт"
                className="input text-sm"
                disabled={recalculating}
              />
            </div>

            {/* Вес */}
            <div>
              <label className="text-[11px] text-ink-dim block mb-1 uppercase tracking-wider">
                Вес / Объём (грамм)
              </label>
              <input
                type="number"
                inputMode="numeric"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="150"
                className="input text-sm"
                disabled={recalculating}
                min={1}
                max={5000}
              />
            </div>

            {/* Кнопка AI-пересчёта */}
            {!manualMode && (
              <button
                onClick={recalculateWithAI}
                disabled={recalculating || !name.trim() || !weight}
                className="w-full py-2.5 rounded-xl bg-emerald-glow/15 border border-emerald-glow/30 text-emerald-glow text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
              >
                {recalculating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    AI пересчитывает…
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    {isNew ? 'Посчитать КБЖУ через AI' : 'Пересчитать через AI'}
                  </>
                )}
              </button>
            )}

            {/* Ошибка AI */}
            {aiError && (
              <div className="bg-danger/10 border border-danger/30 rounded-lg p-2.5 text-xs text-danger flex items-start gap-2">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{aiError}</span>
              </div>
            )}

            {/* Переключатель ручного режима */}
            <div className="flex items-center justify-between py-2 border-t border-line/30">
              <div>
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <Settings size={13} className="text-ink-dim" />
                  Ручной ввод КБЖУ
                </div>
                <div className="text-[10px] text-ink-dim mt-0.5">
                  Для опытных — с упаковки
                </div>
              </div>
              <button
                onClick={() => {
                  setManualMode(!manualMode);
                  haptic('light');
                }}
                className={`w-12 h-6 rounded-full relative transition-colors shrink-0 ${
                  manualMode ? 'bg-emerald-glow' : 'bg-line'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-bg transition-transform ${
                    manualMode ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* КБЖУ поля */}
            <div className={`grid grid-cols-2 gap-2 ${!manualMode ? 'opacity-60' : ''}`}>
              <div>
                <label className="text-[10px] text-ink-dim block mb-1">Калории</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={calories}
                  onChange={(e) => setCalories(e.target.value)}
                  placeholder="0"
                  className="input text-sm py-2"
                  disabled={!manualMode}
                  min={0}
                />
              </div>
              <div>
                <label className="text-[10px] text-ink-dim block mb-1">Белки (г)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={protein}
                  onChange={(e) => setProtein(e.target.value)}
                  placeholder="0"
                  className="input text-sm py-2"
                  disabled={!manualMode}
                  min={0}
                />
              </div>
              <div>
                <label className="text-[10px] text-ink-dim block mb-1">Жиры (г)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={fat}
                  onChange={(e) => setFat(e.target.value)}
                  placeholder="0"
                  className="input text-sm py-2"
                  disabled={!manualMode}
                  min={0}
                />
              </div>
              <div>
                <label className="text-[10px] text-ink-dim block mb-1">Углеводы (г)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={carbs}
                  onChange={(e) => setCarbs(e.target.value)}
                  placeholder="0"
                  className="input text-sm py-2"
                  disabled={!manualMode}
                  min={0}
                />
              </div>
            </div>

            {/* Превью итога */}
            {calories && Number(calories) > 0 && (
              <div className="bg-emerald-glow/5 border border-emerald-glow/20 rounded-xl p-2.5">
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <span className="font-display text-lg font-bold text-emerald-glow">
                      {Math.round(Number(calories))}
                    </span>
                    <span className="text-xs text-ink-muted ml-1">ккал</span>
                  </div>
                  <span className="text-[11px] text-ink-muted">
                    Б{Number(protein || 0).toFixed(0)} · Ж{Number(fat || 0).toFixed(0)} · У{Number(carbs || 0).toFixed(0)}
                  </span>
                </div>
              </div>
            )}

            {/* ───────── КНОПКА УДАЛИТЬ ───────── */}
            {!isNew && onDelete && (
              <button
                onClick={handleDelete}
                disabled={recalculating}
                className="w-full py-2.5 rounded-xl bg-danger/10 border border-danger/30 text-danger text-sm font-medium flex items-center justify-center gap-2"
              >
                <Trash2 size={14} />
                Удалить продукт
              </button>
            )}

            {/* ───────── КНОПКИ ОТМЕНА + ПРИМЕНИТЬ ───────── */}
            {/* Прямо под кнопкой удалить, в самом контенте */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={onClose}
                disabled={recalculating}
                className="flex-1 py-3 rounded-xl bg-bg-elevated text-ink-muted text-sm font-medium disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                onClick={handleApply}
                disabled={!canApply}
                className="flex-[2] py-3 rounded-xl bg-emerald-glow text-bg text-sm font-bold disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.98] transition-all"
              >
                {isNew ? '+ Добавить' : '✓ Применить'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
